import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eq } from 'lodash';

import { SheetsImportInfoResult } from '@futrli-sdk/predict';

import { getImportSheetInfo } from '../../../../../../core/api/external-sheets';

export interface ImportSheetsInfoResult {
  workspaceId: string;
  organisationId: string;
  forecastId?: string;
  error?: string;
  data?: SheetsImportInfoResult;
}

export interface InputDataItem {
  forecastId?: string;
  hasCreatedImportSheet: boolean;
  organisationId: string;
  workspaceId: string;
}

/**
 * Hook that will retrieve the master google sheet info for the given input items
 * @param inputForecastData - Array of input data. Please make sure this is a stable variable (use useState / useMemo if needed),
 *                           otherwise you will get into a loop that causes endless requests.
 */
export const useImportSheetsInfo = (inputItems: InputDataItem[]) => {
  const [idsLoading, setIdsLoading] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, ImportSheetsInfoResult | undefined>>({});
  const requestedData = useRef<Required<InputDataItem>[]>([]);

  const requestSheetInfo = useCallback(async (item: Required<InputDataItem>): Promise<
    ImportSheetsInfoResult | undefined
  > => {
    try {
      const result = await getImportSheetInfo(item.workspaceId, item.organisationId, item.forecastId);
      return {
        ...item,
        error: result.error ? 'Failed to load import Google sheet information' : undefined,
        data: result
      };
    } catch (e) {
      return {
        ...item,
        error: 'Failed to fetch import Google sheet information',
        data: undefined
      };
    }
  }, []);

  const requestAllSheetsInfo = useCallback(
    async (items: Required<InputDataItem>[]) => {
      const output: Record<string, ImportSheetsInfoResult | undefined> = {};
      requestedData.current = items;
      let remainingIds = items.map(item => item.forecastId);
      setIdsLoading(remainingIds);

      const load = async (item: Required<InputDataItem>) => {
        const data = await requestSheetInfo(item);
        if (items === requestedData.current) {
          output[item.forecastId] = data;
          setResults(output);
          remainingIds = remainingIds.filter(forecastId => forecastId !== item.forecastId);
          setIdsLoading(remainingIds);
        }
      };

      items.forEach(item => {
        load(item);
      });
    },
    [requestSheetInfo]
  );

  const refresh = useCallback(() => {
    requestAllSheetsInfo(requestedData.current);
  }, [requestAllSheetsInfo]);

  // Reload data when input parameters change
  useEffect(() => {
    if (inputItems.length) {
      const newInputItems = inputItems.filter(
        item => !!item.forecastId && item.hasCreatedImportSheet
      ) as Required<InputDataItem>[];

      if (!eq(newInputItems, requestedData.current)) {
        requestAllSheetsInfo(newInputItems);
      }
    }
  }, [inputItems, requestAllSheetsInfo]);

  return { isLoading: idsLoading.length > 0, idsLoading, results, refresh };
};

/**
 * Hook that will retrieve the master google sheet info for the given forecast ID
 * @param workspaceId - Workspace ID
 * @param organisationId - Organisation ID
 * @param inputForecastId - Forecast ID
 */
export const useImportSheetInfo = (
  workspaceId: string,
  organisationId: string,
  forecastId: string | undefined,
  hasCreatedImportSheet: boolean
) => {
  const input = useMemo(() => [{ workspaceId, organisationId, forecastId, hasCreatedImportSheet }], [
    workspaceId,
    organisationId,
    forecastId,
    hasCreatedImportSheet
  ]);
  const { isLoading, results, refresh } = useImportSheetsInfo(input);
  return {
    isLoading,
    result: forecastId ? results[forecastId] : undefined,
    refresh
  };
};
